import { RecurrenceUnit } from '@cpce/database/enums';
import { addDays, addMonths, addWeeks } from 'date-fns';
import { Decimal } from 'decimal.js';
import z from 'zod';
import { zDateField } from '../utils/date.utils.js';
import { zodMoney } from './decimal.types.js';
export const RecurrenceUnitSchema = z.enum(RecurrenceUnit);
// Always the plural form: French says "tous les mois" and "toutes les semaines" even for a period
// of one. Gender is carried explicitly — guessing it from a trailing "e" would turn "trimestre"
// feminine.
const RECURRENCE_UNIT_LABELS = {
    [RecurrenceUnit.day]: { plural: 'jours', feminine: false },
    [RecurrenceUnit.week]: { plural: 'semaines', feminine: true },
    [RecurrenceUnit.month]: { plural: 'mois', feminine: false },
    [RecurrenceUnit.quarter]: { plural: 'trimestres', feminine: false },
    [RecurrenceUnit.semester]: { plural: 'semestres', feminine: false },
    [RecurrenceUnit.year]: { plural: 'ans', feminine: false },
};
// "2 semaines", "3 mois" — the unit as it reads next to a count.
export function recurrenceUnitLabel(unit) {
    return RECURRENCE_UNIT_LABELS[unit].plural;
}
// "tous les" / "toutes les", agreeing with the unit. Exported because the sentence is sometimes
// split across form controls ("Répéter toutes les [2] [semaines]") rather than written in one go.
export function recurrenceDeterminer(unit) {
    return RECURRENCE_UNIT_LABELS[unit].feminine ? 'toutes les' : 'tous les';
}
// ISO weekdays, Monday first — the order the mockup's L Ma Me J V S D row uses.
export const WEEKDAYS = [
    { value: 1, short: 'L', label: 'lundi' },
    { value: 2, short: 'Ma', label: 'mardi' },
    { value: 3, short: 'Me', label: 'mercredi' },
    { value: 4, short: 'J', label: 'jeudi' },
    { value: 5, short: 'V', label: 'vendredi' },
    { value: 6, short: 'S', label: 'samedi' },
    { value: 7, short: 'D', label: 'dimanche' },
];
export const recurrenceRuleSchema = z.object({
    unit: RecurrenceUnitSchema,
    count: z.number().int().min(1, 'La répétition doit être au moins de 1'),
    // Only meaningful for `week`; ignored otherwise.
    weekdays: z.array(z.number().int().min(1).max(7)).default([]),
});
// The six fixed options of the dropdown, before any saved custom recurrence. §3.4 of the plan doc:
// the spec's units win over the mockup's "1/2/3/6/9 mois, 1 an" list.
export const FIXED_RECURRENCES = [
    { unit: RecurrenceUnit.day, count: 1, weekdays: [] },
    { unit: RecurrenceUnit.week, count: 1, weekdays: [] },
    { unit: RecurrenceUnit.month, count: 1, weekdays: [] },
    { unit: RecurrenceUnit.quarter, count: 1, weekdays: [] },
    { unit: RecurrenceUnit.semester, count: 1, weekdays: [] },
    { unit: RecurrenceUnit.year, count: 1, weekdays: [] },
];
export const DEFAULT_RECURRENCE = {
    unit: RecurrenceUnit.month,
    count: 1,
    weekdays: [],
};
// Two rules that behave the same must be stored the same: weekdays only mean anything for `week`,
// and their order never did.
export function normalizeRecurrenceRule(rule) {
    return {
        unit: rule.unit,
        count: rule.count,
        weekdays: rule.unit === RecurrenceUnit.week ? [...new Set(rule.weekdays)].sort((a, b) => a - b) : [],
    };
}
// Dedupe key: two recurrences describing the same rule share it, so picking one identical to a
// saved one reuses that row rather than adding a twin to the dropdown.
export function buildRecurrenceSignature(rule) {
    const normalized = normalizeRecurrenceRule(rule);
    return `${normalized.unit}:${normalized.count}:${normalized.weekdays.join(',')}`;
}
// The label is derived, never stored, so it cannot drift from the rule it names.
export function describeRecurrence(rule) {
    const { plural } = RECURRENCE_UNIT_LABELS[rule.unit];
    const determiner = capitalize(recurrenceDeterminer(rule.unit));
    const every = rule.count > 1 ? `${determiner} ${rule.count} ${plural}` : `${determiner} ${plural}`;
    if (rule.unit !== RecurrenceUnit.week || rule.weekdays.length === 0)
        return every;
    const names = [...rule.weekdays]
        .sort((a, b) => a - b)
        .flatMap((day) => {
        const weekday = WEEKDAYS.find((candidate) => candidate.value === day);
        return weekday ? [weekday.label] : [];
    });
    // "le lundi et le jeudi", "le lundi, le mercredi et le vendredi".
    const joined = names.length === 1
        ? `le ${names[0]}`
        : `le ${names.slice(0, -1).join(', le ')} et le ${names[names.length - 1]}`;
    return `${every} ${joined}`;
}
function capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
const MONTHS_PER_UNIT = {
    [RecurrenceUnit.month]: 1,
    [RecurrenceUnit.quarter]: 3,
    [RecurrenceUnit.semester]: 6,
    [RecurrenceUnit.year]: 12,
};
// Instalment 1 is always the date the accountant picked, even when it is not one of the selected
// weekdays; the rest are the rule's occurrences strictly after it. Month-based units go through
// `addMonths` — which clamps 31 January + 1 month to the end of February instead of spilling into
// March — and are computed from the first date rather than cumulatively, so the day of month never
// drifts across a short month.
export function instalmentDates(firstDate, rule, count) {
    if (count <= 0)
        return [];
    const months = MONTHS_PER_UNIT[rule.unit];
    if (months !== undefined) {
        return range(count).map((index) => addMonths(firstDate, months * rule.count * index));
    }
    if (rule.unit === RecurrenceUnit.day) {
        return range(count).map((index) => addDays(firstDate, rule.count * index));
    }
    const days = [...new Set(rule.weekdays)].sort((a, b) => a - b);
    if (days.length === 0) {
        return range(count).map((index) => addWeeks(firstDate, rule.count * index));
    }
    // Walks the selected days inside the current week, then jumps `count` weeks — the Google Agenda
    // behaviour the mockup is modelled on.
    const dates = [firstDate];
    let cursor = firstDate;
    while (dates.length < count) {
        const cursorDay = isoWeekday(cursor);
        const nextDay = days.find((day) => day > cursorDay);
        if (nextDay !== undefined) {
            cursor = addDays(cursor, nextDay - cursorDay);
        }
        else {
            const monday = addDays(cursor, 1 - cursorDay);
            cursor = addDays(addWeeks(monday, rule.count), days[0] - 1);
        }
        dates.push(cursor);
    }
    return dates;
}
function range(count) {
    return Array.from({ length: count }, (_, index) => index);
}
function isoWeekday(date) {
    const day = date.getDay();
    return day === 0 ? 7 : day;
}
// "0004601" + 1 -> "0004602", zero-padding preserved. A non-numeric reference cannot be stepped, so
// only the first instalment carries it and the rest are left for the accountant to fill in.
export function incrementInstrumentNumber(first, index) {
    const trimmed = first.trim();
    if (index === 0)
        return trimmed || null;
    if (!/^\d+$/.test(trimmed))
        return null;
    return String(Number(trimmed) + index).padStart(trimmed.length, '0');
}
// Equal split, with the rounding remainder carried by the **last** instalment: an échéancier reads
// as N equal instalments and a final adjusted one ("11 mensualités de X puis une de Y"), which is
// how a French schedule is normally worded. Every row stays editable afterwards.
export function splitAmount(total, count) {
    if (count <= 0)
        return [];
    const base = total.dividedBy(count).toDecimalPlaces(2, Decimal.ROUND_DOWN);
    const amounts = Array.from({ length: count }, () => base);
    const remainder = total.minus(base.times(count));
    amounts[count - 1] = base.plus(remainder);
    return amounts;
}
// The rows the accountant validated, not the parameters they came from: every date, amount and
// instrument number is editable before validation, so the server is told what was actually agreed
// and re-checks the total rather than regenerating and hoping it matches.
export const paymentScheduleInputSchema = z.object({
    rule: recurrenceRuleSchema,
    // Which saved recurrence it came from, if any. Provenance only — the rule above is authoritative.
    recurrenceId: z.number().int().positive().optional(),
    firstInstalmentDate: zDateField('La date de la 1ère échéance est requise'),
    firstInstrumentNumber: z.string().optional(),
    instalments: z
        .array(z.object({
        number: z.number().int().min(1),
        instrumentNumber: z.string().nullable().optional(),
        dueDate: zDateField("La date d'échéance est requise"),
        amount: zodMoney().refine((value) => value.gt(0), {
            message: "Le montant d'une échéance doit être positif",
        }),
    }))
        .min(1, 'Un échéancier comporte au moins une échéance'),
});
// Shared by the modal's preview and by the server: one implementation, so what the accountant sees
// before validating is what gets written.
export function generateInstalments(params) {
    const { count, firstDate, rule, totalAmount, firstInstrumentNumber } = params;
    const amounts = splitAmount(totalAmount, count);
    const dates = instalmentDates(firstDate, rule, count);
    return amounts.map((amount, index) => ({
        number: index + 1,
        instrumentNumber: firstInstrumentNumber
            ? incrementInstrumentNumber(firstInstrumentNumber, index)
            : null,
        dueDate: dates[index],
        amount,
    }));
}
